/** @format */

import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import type {
	ImportTaxonomyEntry,
	Question,
	QuestionContentBlock,
	SpecificationThemeAggregation,
	UpdateQuestionInput,
} from "@question-bank/shared";
import { Locale } from "../../features/i18n/init";
import { useBrand } from "../../hooks/useBrand";
import { useImportJob } from "../../hooks/useImportJob";
import { useQuestion } from "../../hooks/useQuestion";
import { getAssetUrl, getQuestion, updateQuestion } from "../../api/questions";
import { notify } from "../../components/Toast/toast";
import { ProxiedImage } from "../../components/ProxiedImage";
import { ClassificationReview, type TaxonomySelection } from "../../components/ClassificationReview";
import { ReviewQuestionBlocks } from "../../components/ReviewQuestionBlocks";

interface FormState {
	questionNumber: string;
	maxMarks: string;
	blocks: QuestionContentBlock[];
}

const toFormState = (question: Question): FormState => ({
	questionNumber: question.questionNumber,
	maxMarks: question.maxMarks === undefined ? "" : String(question.maxMarks),
	blocks: question.contentBlocks ?? [],
});

type PdfTab = "paper" | "paperCy" | "markScheme" | "markSchemeCy";

interface PdfChoice {
	id: PdfTab;
	label: string;
	key?: string;
	page?: number;
}

const inputClass = "mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800";

/** The review search needs the aggregations shape. The job stores a flat list. */
const taxonomyAggregations = (entries: readonly ImportTaxonomyEntry[] | undefined): SpecificationThemeAggregation[] => {
	const themes = new Map<string, Map<string, Set<string>>>();
	for (const entry of entries ?? []) {
		const topics = themes.get(entry.theme) ?? new Map<string, Set<string>>();
		const subTopics = topics.get(entry.topic) ?? new Set<string>();
		subTopics.add(entry.subTopic);
		topics.set(entry.topic, subTopics);
		themes.set(entry.theme, topics);
	}
	return [...themes].map(([theme, topics]) => ({
		key: theme,
		count: 0,
		topics: [...topics].map(([topic, subTopics]) => ({
			key: topic,
			count: 0,
			subTopics: [...subTopics].map((subTopic) => ({ key: subTopic, count: 0 })),
		})),
	}));
};

/**
 * Side-by-side review of one import job's questions: the extracted record on the
 * left, the source PDF page it came from on the right. The current question is
 * the `q` search param (1-based) so a reviewer can link to or reload a position.
 */
export const AdminImportReviewPage = () => {
	const { brand } = useBrand();
	const navigate = useNavigate();
	const { locale, id = "" } = useParams<{ locale: Locale; id: string }>();
	const [searchParams] = useSearchParams();
	const { job, isLoading: isJobLoading, error: jobError } = useImportJob(brand, id);

	const questionIds = job?.createdQuestionIds ?? [];
	const requested = Number.parseInt(searchParams.get("q") ?? "1", 10);
	const position = Number.isInteger(requested) ? Math.min(Math.max(requested, 1), Math.max(questionIds.length, 1)) : 1;
	const questionId = questionIds[position - 1] ?? "";

	const { question, isLoading: isQuestionLoading, error: questionError } = useQuestion(brand, questionId);
	const [form, setForm] = useState<FormState | null>(null);
	const [saved, setSaved] = useState<Question | null>(null);
	const [isSaving, setIsSaving] = useState(false);
	const [pdfTab, setPdfTab] = useState<PdfTab>("paper");

	useEffect(() => {
		setSaved(null);
		setForm(question ? toFormState(question) : null);
	}, [question]);

	const backLink = `/${locale}/admin/import-jobs/${id}`;
	const goTo = (target: number) => navigate(`/${locale}/admin/import-jobs/${id}/review?q=${target}`);

	if (isJobLoading) {
		return (
			<p role="status" className="text-sm text-slate-600">
				Loading import job…
			</p>
		);
	}

	if (jobError || !job) {
		return (
			<div role="alert" className="rounded-lg border border-red-500 bg-red-100 p-6">
				<p className="font-semibold text-red-700">Failed to load import job</p>
				<p className="mt-1 text-sm text-red-700">{jobError ?? "Import job not found"}</p>
				<Link to={backLink} className="mt-4 inline-block text-sm font-semibold hover:underline">
					← Back to import job
				</Link>
			</div>
		);
	}

	if (questionIds.length === 0) {
		return (
			<section className="space-y-4">
				<Link to={backLink} className="text-sm font-semibold hover:underline">
					← Back to import job
				</Link>
				<p className="text-sm text-slate-600">This import has no questions to review.</p>
			</section>
		);
	}

	const current = saved ?? question;
	const pdfChoices: PdfChoice[] = [
		{ id: "paper", label: "Question paper", key: job.sourceKeys?.paper, page: current?.sourcePage },
		...(job.sourceKeys?.paperCy
			? [
					{
						id: "paperCy" as const,
						label: "Question paper - Welsh",
						key: job.sourceKeys.paperCy,
						page: current?.sourcePage,
					},
				]
			: []),
		{ id: "markScheme", label: "Mark scheme", key: job.sourceKeys?.markScheme, page: current?.markSchemePage },
		...(job.sourceKeys?.markSchemeCy
			? [
					{
						id: "markSchemeCy" as const,
						label: "Mark scheme - Welsh",
						key: job.sourceKeys.markSchemeCy,
						page: current?.markSchemePage,
					},
				]
			: []),
	];
	const activePdf = pdfChoices.find((choice) => choice.id === pdfTab) ?? pdfChoices[0];
	const isMarkSchemeTab = activePdf.id === "markScheme" || activePdf.id === "markSchemeCy";
	const pdfUrl = activePdf.key && activePdf.page ? `${getAssetUrl(brand, activePdf.key)}#page=${activePdf.page}` : null;
	const markScheme = current?.markSchemeContent;
	const markSchemeImage = current?.en?.markSchemeImage;
	const examinersComments = current?.en?.examinersComments;

	const buildInput = (status?: "staged"): UpdateQuestionInput | null => {
		if (!form || !current) {
			return null;
		}
		const maxMarks = form.maxMarks.trim() === "" ? undefined : Number(form.maxMarks);
		return {
			brand,
			questionNumber: form.questionNumber,
			maxMarks,
			contentBlocks: form.blocks,
			...(status ? { status } : {}),
		} as UpdateQuestionInput;
	};

	const save = async (event: FormEvent | null, status?: "staged") => {
		event?.preventDefault();
		const input = buildInput(status);
		if (!input || !current) {
			return;
		}
		if (input.maxMarks !== undefined && (!Number.isInteger(input.maxMarks) || input.maxMarks <= 0)) {
			notify("error", "Max marks must be a whole number greater than 0");
			return;
		}
		setIsSaving(true);
		try {
			await updateQuestion(current.id, brand, input);
			setSaved({
				...current,
				questionNumber: form?.questionNumber ?? current.questionNumber,
				maxMarks: input.maxMarks,
				contentBlocks: form?.blocks,
				status: status ?? current.status,
			});
			notify("success", status ? "Question accepted and staged" : "Question saved");
			if (status && position < questionIds.length) {
				goTo(position + 1);
			}
		} catch (err) {
			notify("error", err instanceof Error ? err.message : "Save failed. Please try again");
		} finally {
			setIsSaving(false);
		}
	};

	const acceptAllDrafts = async () => {
		setIsSaving(true);
		let accepted = 0;
		try {
			for (const targetId of questionIds) {
				const loaded = targetId === current?.id && current ? current : await getQuestion(brand, targetId);
				if (loaded.status !== "draft") {
					continue;
				}
				await updateQuestion(targetId, brand, { brand, status: "staged" } as UpdateQuestionInput);
				accepted += 1;
				if (targetId === current?.id) {
					setSaved({ ...loaded, status: "staged" });
				}
			}
			notify("success", `Accepted ${accepted} drafts`);
		} catch (err) {
			notify("error", err instanceof Error ? err.message : "Accept all failed. Please try again");
		} finally {
			setIsSaving(false);
		}
	};

	const selectClassification = async (selection: TaxonomySelection) => {
		if (!current) {
			return;
		}
		await updateQuestion(current.id, brand, {
			brand,
			specification: { theme: selection.theme, topic: selection.topic, subTopic: selection.subTopic },
		} as UpdateQuestionInput);
		setSaved({
			...current,
			specification: { theme: selection.theme, topic: selection.topic, subTopic: selection.subTopic },
		});
	};

	return (
		<section className="space-y-4">
			<div className="flex flex-wrap items-center justify-between gap-3">
				<div>
					<Link to={backLink} className="text-sm font-semibold hover:underline">
						← Back to import job
					</Link>
					<h2 className="mt-1 text-[1.75rem] font-bold text-(--admin-color-primary)">Review question</h2>
					{current && (
						<Link
							to={`/${locale}/admin/questions/${current.id}`}
							className="text-sm font-semibold text-(--admin-color-primary-strong) hover:underline"
						>
							Open question
						</Link>
					)}
				</div>
				<button
					type="button"
					onClick={() => void acceptAllDrafts()}
					disabled={isSaving}
					className="rounded-md bg-(--admin-color-primary) px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
				>
					Accept all drafts
				</button>
				<nav aria-label="Question navigation" className="flex items-center gap-3">
					<button
						type="button"
						onClick={() => goTo(position - 1)}
						disabled={position <= 1}
						className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-semibold disabled:opacity-50"
					>
						Previous
					</button>
					<span aria-live="polite" className="text-sm text-slate-700">
						Question {position} of {questionIds.length}
					</span>
					<button
						type="button"
						onClick={() => goTo(position + 1)}
						disabled={position >= questionIds.length}
						className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-semibold disabled:opacity-50"
					>
						Next
					</button>
				</nav>
			</div>

			<div role="tablist" aria-label="Review content" className="flex flex-wrap gap-2">
				{pdfChoices.map((choice) => (
					<button
						key={choice.id}
						type="button"
						role="tab"
						aria-selected={activePdf.id === choice.id}
						onClick={() => setPdfTab(choice.id)}
						className={`rounded-md px-3 py-1 text-sm font-semibold ${activePdf.id === choice.id ? "bg-(--admin-color-primary) text-white" : "border border-slate-200 bg-white text-slate-700"}`}
					>
						{choice.label}
					</button>
				))}
			</div>

			<div className="grid gap-4 lg:grid-cols-2">
				<div className="rounded-lg border border-slate-200 bg-white p-6 shadow-(--admin-shadow-raised)">
					{isQuestionLoading && (
						<p role="status" className="text-sm text-slate-600">
							Loading question…
						</p>
					)}
					{questionError && (
						<p role="alert" className="text-sm text-red-700">
							{questionError}
						</p>
					)}
					{current && isMarkSchemeTab && (
						<section aria-label="Mark scheme content" className="space-y-4 text-sm text-slate-700">
							{!markScheme && !markSchemeImage && !examinersComments && (
								<p>No mark scheme content was extracted for this question.</p>
							)}
							{markScheme?.prompt && <p className="whitespace-pre-wrap">{markScheme.prompt}</p>}
							{markScheme?.marks !== null && markScheme?.marks !== undefined && (
								<p>
									<span className="font-medium">Marks:</span> {markScheme.marks}
								</p>
							)}
							{markScheme?.ao && Object.keys(markScheme.ao).length > 0 && (
								<p>
									<span className="font-medium">Assessment objectives:</span>{" "}
									{Object.entries(markScheme.ao)
										.map(([ao, marks]) => `${ao}: ${marks}`)
										.join(", ")}
								</p>
							)}
							{markScheme?.markCodes && markScheme.markCodes.length > 0 && (
								<p>
									<span className="font-medium">Mark codes:</span> {markScheme.markCodes.join(" ")}
								</p>
							)}
							{markScheme?.blocks?.map((block, index) => {
								if (block.type === "text") {
									return (
										<p key={index} className="whitespace-pre-wrap">
											{block.en}
										</p>
									);
								}
								if (block.type === "asset") {
									const key = block.en ?? block.cy;
									return key ? (
										<ProxiedImage key={index} src={getAssetUrl(brand, key)} alt="Figure" className="max-w-full" />
									) : null;
								}
								return (
									<table key={index} className="w-full border-collapse border border-slate-300">
										<tbody>
											{block.rows.map((row, r) => (
												<tr key={r}>
													{row.map((cell, c) => (
														<td key={c} className="border border-slate-300 p-1 align-top">
															{cell}
														</td>
													))}
												</tr>
											))}
										</tbody>
									</table>
								);
							})}
							{markScheme?.comments && markScheme.comments.length > 0 && (
								<div>
									<h3 className="font-medium">Comments</h3>
									<ul className="list-disc pl-5">
										{markScheme.comments.map((comment, index) => (
											<li key={index}>{comment}</li>
										))}
									</ul>
								</div>
							)}
							{markSchemeImage && (
								<ProxiedImage src={getAssetUrl(brand, markSchemeImage)} alt="Mark scheme" className="max-w-full" />
							)}
							{examinersComments && <p className="whitespace-pre-wrap">{examinersComments}</p>}
						</section>
					)}
					{form && current && !isMarkSchemeTab && (
						<form onSubmit={(event) => void save(event)} className="space-y-4" aria-label="Extracted question">
							<p className="text-sm text-slate-600">
								Status: <strong>{current.status}</strong>
							</p>
							<label className="block text-sm font-medium text-slate-700">
								Question number
								<input
									className={inputClass}
									value={form.questionNumber}
									onChange={(e) => setForm({ ...form, questionNumber: e.target.value })}
								/>
							</label>
							<label className="block text-sm font-medium text-slate-700">
								Max marks
								<input
									className={inputClass}
									inputMode="numeric"
									value={form.maxMarks}
									onChange={(e) => setForm({ ...form, maxMarks: e.target.value })}
								/>
							</label>
							<ReviewQuestionBlocks
								brand={brand}
								blocks={form.blocks}
								onChange={(blocks) => setForm((prev) => (prev ? { ...prev, blocks } : prev))}
							/>
							{current.classification && (
								<ClassificationReview
									proposal={current.classification}
									current={current.specification}
									taxonomy={taxonomyAggregations(job.taxonomy)}
									onSelect={selectClassification}
								/>
							)}
							<div className="flex gap-3">
								<button
									type="submit"
									disabled={isSaving}
									className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold disabled:opacity-50"
								>
									Save changes
								</button>
								<button
									type="button"
									disabled={isSaving || current.status !== "draft"}
									onClick={() => void save(null, "staged")}
									className="rounded-md bg-(--admin-color-primary) px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
								>
									Accept
								</button>
							</div>
						</form>
					)}
				</div>

				<div className="rounded-lg border border-slate-200 bg-white p-2 shadow-(--admin-shadow-raised)">
					{pdfUrl ? (
						<iframe
							key={pdfUrl}
							title={`${activePdf.label} PDF page ${activePdf.page}`}
							src={pdfUrl}
							className="h-[75vh] w-full"
						/>
					) : (
						<p className="p-4 text-sm text-slate-600">
							{isQuestionLoading
								? "Loading source page…"
								: `No ${activePdf.label.toLowerCase()} page is recorded for this question.`}
						</p>
					)}
				</div>
			</div>
		</section>
	);
};
